'use client';

import { Box, freddyTheme, Link, ListItem } from '@coolblue-development/freddy';
import selectPropertyTrigger from '@coolblue-development/ga4-events/triggers/AlternativePrimaryItem/selectPropertyTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import type { DistinctProperty } from '@features/product/typings';
import { useState } from 'react';

import { stickyTitleStyles } from '../BetterOptionsBlock.styled';
import PropertyValueRow from './PropertyValueRow';

type Props = {
  displayName: string;
  values: DistinctProperty[];
  productTypeId: string | null;
};

const PropertyRow = ({ displayName, values, productTypeId }: Props) => {
  const { trackEvent } = useAnalytics();

  const [showPropertyExplanation, setShowPropertyExplanation] = useState<boolean>(() => false);

  const params = {
    property_name: displayName,
    property_value: values.join(', ')
  };

  const hasExplanation = values[0].hasExplanation ?? false;
  const properties = values.map(value => ({
    displayName: value.displayName ?? '',
    hasExplanation: value.hasExplanation ?? false,
    propertyId: value.propertyId ?? '',
    value: value.value
  }));

  function togglePropertyExplanationModal(open: boolean) {
    if (!hasExplanation || open === showPropertyExplanation) return;
    setShowPropertyExplanation(open);
  }

  return (
    <>
      <ListItem
        sx={{
          borderTop: `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.base}`
        }}
      >
        <Box
          py={200}
          px={300}
          sx={{
            position: 'relative',
            '&:before': {
              width: `calc(100% / ${values.length})`,
              height: '100%',
              position: 'absolute',
              left: 0,
              top: 0,
              backgroundColor: freddyTheme.color.background.secondary,
              content: '""'
            }
          }}
        >
          {hasExplanation ? (
            <Link
              fontWeight={700}
              onClick={() => {
                trackEvent(selectPropertyTrigger(params));
                togglePropertyExplanationModal(true);
              }}
              sx={{
                ...stickyTitleStyles,
                display: 'inline-block',
                maxWidth: `calc(calc(100% / ${values.length}) - ${freddyTheme.spacing[200]})`,
                width: '100%'
              }}
            >
              {displayName}
            </Link>
          ) : (
            <Box
              sx={{
                ...stickyTitleStyles,
                display: 'inline-block',
                maxWidth: `calc(calc(100% / ${values.length}) - ${freddyTheme.spacing[200]})`,
                width: '100%',
                fontWeight: 700
              }}
            >
              {displayName}
            </Box>
          )}
        </Box>

        <PropertyValueRow values={values} />
      </ListItem>
      {hasExplanation && (
        <PropertyExplanationModal
          open={showPropertyExplanation}
          onClose={() => togglePropertyExplanationModal(false)}
          property={properties[0]}
          productTypeId={productTypeId ?? undefined}
        />
      )}
    </>
  );
};

export default PropertyRow;
